import { useEffect } from "react";
import { useSelector } from "react-redux";

import UpgradeOverlay from "../../../common/UpgradeOverlay";
import { upgradeMessage, userIsRestrictedFromPortfolio } from "../../../../hooks/useCommercialAccess";

/**
 * "Properties in portfolio" — the Investor Portfolio filter.
 *
 * For new Lite/Pro subscribers this stays on screen but is inert: the inputs
 * are disabled, the block is blurred behind an upgrade CTA, and no portfolio
 * count is rendered. Legend and grandfathered subscribers see it unchanged.
 *
 * memData is read from the store by default so the dozen existing call sites
 * do not each have to thread it through; pass it explicitly in tests.
 */
export default function PortfolioField({
  register,
  fieldNamePrefix = "",
  setValue,
  watch,
  memData: memDataProp,
  portfolioCount,
}) {
  const memDataFromStore = useSelector(
    (state) => state.fetchSiteSettings?.memData
  );
  const memData = memDataProp !== undefined ? memDataProp : memDataFromStore;
  const isRestricted = userIsRestrictedFromPortfolio(memData);

  const minPortfolio = watch(`${fieldNamePrefix}_properties_in_portfolio_min`);
  const maxPortfolio = watch(`${fieldNamePrefix}_properties_in_portfolio_max`);

  useEffect(() => {
    // A restricted plan must not be able to push a portfolio bound into the
    // search, so the mirroring that feeds the other scopes stops here too.
    if (isRestricted) return;

    if (setValue && minPortfolio !== undefined) {
      setValue("properties_in_portfolio_min", minPortfolio);
      setValue("owner_properties_in_portfolio_min", minPortfolio);
      setValue("recommend_properties_in_portfolio_min", minPortfolio);
    }
  }, [minPortfolio, setValue, isRestricted]);

  useEffect(() => {
    if (isRestricted) return;

    if (setValue && maxPortfolio !== undefined) {
      setValue("properties_in_portfolio_max", maxPortfolio);
      setValue("owner_properties_in_portfolio_max", maxPortfolio);
      setValue("recommend_properties_in_portfolio_max", maxPortfolio);
    }
  }, [maxPortfolio, setValue, isRestricted]);

  return (
    <div
      className="form_blk"
      style={isRestricted ? { position: "relative" } : undefined}
    >
      <h6>
        Properties in portfolio
        {/* The count is deliberately absent while restricted — the customer
            should not be able to read portfolio sizes off a locked filter. */}
        {!isRestricted && portfolioCount !== undefined && portfolioCount !== null && (
          <span className="portfolio_count"> ({Number(portfolioCount).toLocaleString('en-US')})</span>
        )}
      </h6>
      <div className="half_flex">
        <div className="half_field">
          <input
            type="text"
            name={`${fieldNamePrefix}_properties_in_portfolio_min`}
            id={`${fieldNamePrefix}_properties_in_portfolio_min`}
            className="input"
            placeholder="Min"
            disabled={isRestricted}
            {...register(`${fieldNamePrefix}_properties_in_portfolio_min`)}
          />
        </div>
        <span></span>
        <div className="half_field">
          <input
            type="text"
            name={`${fieldNamePrefix}_properties_in_portfolio_max`}
            id={`${fieldNamePrefix}_properties_in_portfolio_max`}
            className="input"
            placeholder="Max"
            disabled={isRestricted}
            {...register(`${fieldNamePrefix}_properties_in_portfolio_max`)}
          />
        </div>
      </div>

      {isRestricted && (
        <UpgradeOverlay
          title="Investor Portfolio"
          message={upgradeMessage(memData, "for access", "Investor Portfolio is not included on your plan.")}
          compact
          testId="portfolio-upgrade-overlay"
        />
      )}
    </div>
  );
}
